import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { generatePath } from 'react-router'
import { useLazyQuery } from '@apollo/client'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'

import { userGql, ppuMotorGql } from '@/gql'
import { PolicyDetailProps, SearchInputProps, InputProps } from './types'
import policyDetailAtom from '@/recoil/policyDetail'
import policyAtom from '@/recoil/policy'
import deviceDetailAtom from '@/recoil/deviceDetail'
import tviConnectMainSimPackageAtom from '@/recoil/tviConnectMainSimPackage'
import CONSTANT from '@/constant'
import { toThaiLocaleDateString } from '@/libs/date'
import { UserPolicyInterface } from '@/gql/ppuMotor/getAllUserPoliciesByIdentityId/interfaces'

export enum SearchStateEnum {
  NONE = 'NONE',
  SEARCHING = 'SEARCHING',
  FINISH = 'FINISH',
}

function SearchPolicyBoxHook() {
  const [input, setInput] = useState<InputProps>({
    value: null,
    type: null,
  })
  const [searchState, setSearchState] = useState<SearchStateEnum>(SearchStateEnum.NONE)
  const { searchInput } = useParams<SearchInputProps>()
  const navigate = useNavigate()
  const setPolicyDetail = useSetRecoilState(policyDetailAtom)
  const policy = useRecoilValue(policyAtom)
  const [
    backofficeSearchUserQuery,
    {
      loading: backofficeSearchUserQueryLoading,
      data: backofficeSearchUserQueryData,
      error: backofficeSearchUserQueryError,
    },
  ] = useLazyQuery(userGql.query.backofficeSearchUser, {
    fetchPolicy: 'no-cache',
  })
  const [
    fetchGetAllUserPoliciesByIdentityId,
    {
      loading: fetchGetAllUserPoliciesByIdentityIdLoading,
      data: fetchGetAllUserPoliciesByIdentityIdData,
      error: fetchGetAllUserPoliciesByIdentityIdError,
    },
  ] = useLazyQuery(ppuMotorGql.query.getAllUserPoliciesByIdentityId, {
    fetchPolicy: 'no-cache',
  })
  const [
    fetchDriverBoundPolicies,
    {
      loading: fetchDriverBoundPoliciesLoading,
      data: fetchDriverBoundPoliciesData,
      error: fetchDriverBoundPoliciesError,
    },
  ] = useLazyQuery(ppuMotorGql.query.backofficeGetDriverBoundPoliciesByMobileNumber, {
    fetchPolicy: 'no-cache',
  })
  const resetPolicy = useResetRecoilState(policyAtom)
  const resetPolicyDetail = useResetRecoilState(policyDetailAtom)
  const resetDeviceDetail = useResetRecoilState(deviceDetailAtom)
  const resetTviConnectMainPackage = useResetRecoilState(tviConnectMainSimPackageAtom)
  const isLoading =
    backofficeSearchUserQueryLoading ||
    fetchGetAllUserPoliciesByIdentityIdLoading ||
    fetchDriverBoundPoliciesLoading // TODO add fetch user notification
  const isError =
    fetchGetAllUserPoliciesByIdentityIdError ||
    fetchDriverBoundPoliciesError ||
    backofficeSearchUserQueryError

  useEffect(() => {
    if (searchInput) {
      handleSearch(searchInput)
    }
  }, [searchInput])

  useEffect(() => {
    if (backofficeSearchUserQueryData && backofficeSearchUserQueryData.backofficeSearchUser) {
      const { foreignCitizen, thaiCitizen, isDriverAccount, driverAccountMobileNumber } =
        backofficeSearchUserQueryData.backofficeSearchUser

      // Driver search: backofficeSearchUser returns the OWNER, so the owner's
      // full portfolio (getAllUserPoliciesByIdentityId) is the wrong set —
      // fetch only the policies this driver is actually bound to instead.
      if (isDriverAccount && driverAccountMobileNumber) {
        fetchDriverBoundPolicies({
          variables: {
            mobileNumber: driverAccountMobileNumber,
          },
        })
      } else {
        const identityId = foreignCitizen?.passportId || thaiCitizen?.identityId

        if (identityId) {
          fetchGetAllUserPoliciesByIdentityId({
            variables: {
              identityId: identityId,
            },
          })
        }
      }
    }
  }, [backofficeSearchUserQueryData])

  // Owner path — non-driver searches.
  useEffect(() => {
    if (fetchGetAllUserPoliciesByIdentityIdData) {
      buildPolicyDetail(fetchGetAllUserPoliciesByIdentityIdData.getAllUserPoliciesByIdentityId)
    }
  }, [fetchGetAllUserPoliciesByIdentityIdData])

  // Driver path — the bound subset of policies for the searched driver.
  useEffect(() => {
    if (fetchDriverBoundPoliciesData) {
      buildPolicyDetail(
        fetchDriverBoundPoliciesData.backofficeGetDriverBoundPoliciesByMobileNumber,
      )
    }
  }, [fetchDriverBoundPoliciesData])

  useEffect(() => {
    if (
      backofficeSearchUserQueryError ||
      fetchGetAllUserPoliciesByIdentityIdError ||
      fetchDriverBoundPoliciesError
    ) {
      setSearchState(SearchStateEnum.NONE)
      resetPolicy()
      resetPolicyDetail()
      resetDeviceDetail()
      resetTviConnectMainPackage()
    }
  }, [
    backofficeSearchUserQueryError,
    fetchGetAllUserPoliciesByIdentityIdError,
    fetchDriverBoundPoliciesError,
  ])

  // Builds the policyDetail recoil object from the searched user + whichever
  // policy set was fetched (owner portfolio or driver-bound subset). For a
  // driver search the user fields belong to the OWNER; the driver is carried
  // only by the driverAccount* fields.
  const buildPolicyDetail = (policies: UserPolicyInterface[]) => {
    if (!backofficeSearchUserQueryData || !backofficeSearchUserQueryData.backofficeSearchUser) {
      return
    }

    const {
      id,
      foreignCitizen,
      thaiCitizen,
      firstname,
      lastname,
      mobileNumber,
      email,
      createdAt,
      deleteRequestedAt,
      isDriverAccount,
      driverAccountId,
      driverAccountMobileNumber,
      driverAccountRegisteredAt,
    } = backofficeSearchUserQueryData.backofficeSearchUser

    const policyDetail: PolicyDetailProps = {
      id: id,
      insuredName: `${firstname} ${lastname}`,
      identityId: foreignCitizen?.passportId || thaiCitizen?.identityId,
      mobileNumber: mobileNumber,
      email: email,
      registeredDate: createdAt ? toThaiLocaleDateString({ date: createdAt }) : '-',
      policies:
        input.type === CONSTANT.SEARCH_POLICY_BOX.SEARCH_USER_INPUT_TYPE_POLICY_NUMBER
          ? policies.filter((policy: UserPolicyInterface) => policy.policyNumber === input.value)
          : policies,
      deleteRequestedAt: deleteRequestedAt
        ? toThaiLocaleDateString({ date: deleteRequestedAt })
        : null,
      isDriverAccount: isDriverAccount ?? false,
      driverAccountId: driverAccountId ?? null,
      driverAccountMobileNumber: driverAccountMobileNumber ?? null,
      driverAccountRegisteredDate: driverAccountRegisteredAt
        ? toThaiLocaleDateString({ date: driverAccountRegisteredAt })
        : null,
    }

    setPolicyDetail(policyDetail)
    setSearchState(SearchStateEnum.FINISH)
  }

  const handleSetSearchState = (state: SearchStateEnum) => setSearchState(state)

  const handleSearch = (value: any) => {
    if (value) {
      const input = {
        name: '',
        identityId: '',
        policyNumber: '',
        mobileNumber: '',
      }

      navigate(generatePath(CONSTANT.ROUTES.POLICY_DETAIL_PAGE, { searchInput: value }))

      if (isNaN(value)) {
        input.name = value
        setInput({
          value: value,
          type: CONSTANT.SEARCH_POLICY_BOX.SEARCH_USER_INPUT_TYPE_NAME,
        })
      } else {
        if (value.length === 16) {
          input.policyNumber = value
          setInput({
            value: value,
            type: CONSTANT.SEARCH_POLICY_BOX.SEARCH_USER_INPUT_TYPE_POLICY_NUMBER,
          })
        } else if (value.length === 13) {
          input.identityId = value
          setInput({
            value: value,
            type: CONSTANT.SEARCH_POLICY_BOX.SEARCH_USER_INPUT_TYPE_IDENTITY_ID,
          })
        } else if (value.length === 10) {
          input.mobileNumber = value
          setInput({
            value: value,
            type: CONSTANT.SEARCH_POLICY_BOX.SEARCH_USER_INPUT_TYPE_MOBILE_NUMBER,
          })
        }
      }

      backofficeSearchUserQuery({
        variables: {
          input,
        },
      })
    }
    setSearchState(SearchStateEnum.SEARCHING)
    resetPolicy()
    resetPolicyDetail()
    resetDeviceDetail()
    resetTviConnectMainPackage()
  }

  return {
    policy,
    handleSearch,
    isError,
    isLoading,
    searchState,
    handleSetSearchState,
  }
}

export default SearchPolicyBoxHook
